// AI Generated Component
import { Component, Input, OnInit } from '@angular/core';
import { PageService } from '@client/app/services/entities/page/page.service';
import { DateTimeService } from '@client/app/services/date-time/date-time.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { PipeService } from '@client/app/filters/pipe-transformation.service';
import { safeClone } from '@client/app/shared/utils/object-utils';

@Component({
    selector: 'page-details-dashboard-charts-statistics',
    standalone: false,
    template: `
        <mon-chart-line [height]="300"
                    [data]="lineChartData"
                    [labels]="lineChartLabels"
                    [series]="lineChartSeries"
                    [colors]="lineChartColors"
                    [override]="lineChartOverride"
                    [options]="lineChartOptions">
        </mon-chart-line>
    `,
})
export class PageDetailsDashboardChartsStatisticsComponent implements OnInit {
    @Input() page: any;
    @Input() callback: any;

    loading = false;
    lineChartSeries: any[] = [];
    lineChartData: any[] = [];
    lineChartColors: any[] = [];
    lineChartLabels: any[] = [];
    lineChartOverride: any[] = [];
    lineChartOptions: any = {};
    date: any = {};
    referring: any[] = [];
    direct: any[] = [];
    paid: any[] = [];
    organic: any[] = [];
    social: any[] = [];
    type: string = '';

    constructor (
        private pageService: PageService,
        private dateTimeService: DateTimeService,
        private translateService: TranslateService,
        private pipeService: PipeService,
    ) {}

    ngOnInit (): void {
        this.date = {
            endDate: this.dateTimeService.endOf(this.dateTimeService.substractDuration(undefined, 1, 'day' as any), 'day'),
            startDate: this.dateTimeService.substractDuration(undefined, 7, 'day' as any),
        };
        this.setupLineChart();
    }

    private setupLineChart (): void {
        const params = {
            from: this.dateTimeService.format(this.date.startDate, 'YYYY-MM-DD'),
            to: this.dateTimeService.format(this.date.endDate, 'YYYY-MM-DD'),
        };

        const promises = [
            this.pageService.getStatVisitors(this.page.id, safeClone({ ...params, type: 'referring' })),
            this.pageService.getStatVisitors(this.page.id, safeClone({ ...params, type: 'direct' })),
            this.pageService.getStatVisitors(this.page.id, safeClone({ ...params, type: 'paid' })),
            this.pageService.getStatVisitors(this.page.id, safeClone({ ...params, type: 'organic' })),
            this.pageService.getStatVisitors(this.page.id, safeClone({ ...params, type: 'social' })),
        ];

        this.loading = true;
        this.lineChartSeries = [this.translateService.getString('Visits')];
        this.lineChartData = [[]];
        this.lineChartColors = ['#50a6fb'];
        this.lineChartLabels = [];
        this.lineChartOverride = [
            {
                type: 'line',
                fill: false,
                lineTension: 0,
                pointRadius: 0,
                pointHitRadius: 10,
                borderColor: '#50a6fb',
            },
        ];

        this.lineChartOptions = {
            tooltips: {
                mode: 'label',
                intersect: false,
                callbacks: {
                    title: (tooltipItem: any, data: any) => {
                        return data.labels[tooltipItem[0].index];
                    },
                    label: (tooltipItem: any, data: any) => {
                        return (
                            ' ' +
                            this.translateService.getString('Visits') +
                            ': ' +
                            this.pipeService.get('monNumber')(data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index], '0,0')
                        );
                    },
                },
            },
            scales: {
                yAxes: [
                    {
                        id: 'y-axis-0',
                        type: 'linear',
                        position: 'left',
                        ticks: {
                            beginAtZero: true,
                            suggestedMax: 4,
                            maxTicksLimit: 5,
                            callback: (value: any, index: any) => {
                                if (this.type === 'avgVisitTime') {
                                    return this.pipeService.get('monNumber')(value, '00:00:00');
                                } else if (this.type === 'bounceRate') {
                                    return parseInt(value) + '%';
                                }
                                return this.pipeService.get('monNumber')(value, '0,0');
                            },
                            min: 0,
                        },
                    },
                ],
                xAxes: [
                    {
                        gridLines: {
                            display: false,
                        },
                        ticks: {
                            callback: (value: any) => {
                                if (value) {
                                    return this.dateTimeService.format(value, 'MMM DD', 'll');
                                }
                                return '';
                            },
                        },
                    },
                ],
            },
            legend: {
                display: false,
            },
        };

        Promise.all(promises)
            .then((data: any[]) => {
                this.referring = data[0];
                this.direct = data[1];
                this.paid = data[2];
                this.organic = data[3];
                this.social = data[4];

                for (let k = 0; k < this.referring.length; k++) {
                    this.lineChartLabels.push(this.pipeService.get('userDate')(this.referring[k].date, 'll'));
                    this.lineChartData[0].push(
                        this.getVisits(this.referring[k]) +
                            this.getVisits(this.organic[k]) +
                            this.getVisits(this.direct[k]) +
                            this.getVisits(this.paid[k]) +
                            this.getVisits(this.social[k]),
                    );
                }
            })
            .catch(() => {
                // Handle error silently like the original
            })
            .finally(() => {
                this.loading = false;
            });
    }

    private getVisits (statistic: any): number {
        if (typeof statistic === 'undefined') {
            return 0;
        }
        return Number(statistic.visits || 0);
    }
}
